import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Loader2, GripVertical, Save } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import { courseService } from "../../services/courseService";
import { Course } from "./CourseTable";

interface ReorderCoursesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SortableCourseItemProps {
  course: Course;
}

function SortableCourseItem({ course }: SortableCourseItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: course._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg mb-2 ${
        isDragging ? "shadow-lg ring-2 ring-[#e07828]" : "hover:border-gray-300"
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
      >
        <GripVertical size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-[#102040] truncate">{course.title}</h4>
        <p className="text-xs text-gray-500 truncate">
          {course.instructor && typeof course.instructor !== "string"
            ? `${course.instructor.user?.firstName || ""} ${
                course.instructor.user?.lastName || ""
              }`
            : "Unknown Instructor"}
        </p>
      </div>
      <div className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">
        #{course.orderIndex ?? 0}
      </div>
    </div>
  );
}

export function ReorderCoursesDialog({
  isOpen,
  onClose,
  onSuccess,
}: ReorderCoursesDialogProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { showSuccess, showError } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isOpen) {
      fetchCourses();
    }
  }, [isOpen]);

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      const response = await courseService.getAllCourses({
        limit: 100,
        sortBy: "orderIndex",
        sortOrder: "asc",
        isPublished: "true",
      });
      setCourses(response.data?.courses || []);
    } catch (error) {
      console.error("Failed to fetch courses for reordering:", error);
      showError("Failed to load courses");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCourses((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const reorderedData = courses.map((course, index) => ({
        id: course._id,
        orderIndex: index + 1,
      }));

      await courseService.reorderCourses(reorderedData);
      showSuccess("Courses reordered successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to save course order:", error);
      showError("Failed to save new order");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reorder Courses"
      className="max-h-[85vh] flex flex-col"
    >
      <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[60vh] py-2">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={courses.map((c) => c._id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {courses.map((course) => (
                  <SortableCourseItem key={course._id} course={course} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t mt-4">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Order
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
}
